// Explore Gyan Dhara — role-neutral profile
// Figma frames: 132:3 Overview, 133:2 Learning Experiences, 134:2 How It Works
// Additional: VR Experiences (uses 133:2 data), Web Learning Hub (from Student pattern), Access Request

export const EXPLORE_NAV = [
  { label: 'Overview',          to: '/explore' },
  { label: 'VR Experiences',    to: '/explore/vr-experiences' },
  { label: 'Web Learning Hub',  to: '/explore/web-learning-hub' },
];

export const EXPLORE_CTA = {
  primary:   { label: 'Request Access',   to: '/explore/access-request' },
  secondary: { label: 'Choose Your Role', to: '/' },
};

export const EXPLORE_FOOTER = {
  brandNote: 'Conceptualized, designed and developed by Dr. Keyur Sorathia & team at the Embedded Interaction Lab, Department of Design, IIT Guwahati.',
  cols: [
    { title: 'Explore', items: [
      { label: 'Overview',           to: '/explore' },
      { label: 'VR Experiences',     to: '/explore/vr-experiences' },
      { label: 'Web Learning Hub',   to: '/explore/web-learning-hub' },
      { label: 'Request Access',     to: '/explore/access-request' },
    ]},
    { title: 'When you are ready', items: [
      { label: 'Choose Your Role',   to: '/' },
      { label: 'Student or Parent',  to: '/student-parent' },
      { label: 'School or Institution', to: '/school' },
      { label: 'Coaching Institute', to: '/coaching' },
    ]},
  ],
};

// ===============================================================
// 132:3 OVERVIEW
// ===============================================================
export const EXPLORE_OVERVIEW = {
  hero: {
    eyebrow: 'EXPLORE WITHOUT SELECTING A ROLE',
    title:   'Understand Gyan Dhara before choosing how to use it',
    desc:    'Explore the learning ecosystem, curriculum modules, exploratory journeys and operating model without committing to a school, family, research or partnership pathway.',
    ctaPrimary:   { label: 'Explore VR Experiences', to: '/explore/vr-experiences' },
    ctaSecondary: { label: 'Choose Your Role',       to: '/' },
    videoTag: 'GYAN DHARA IN 75 SECONDS',
    videoDesc:'The full explainer video showing VR modules, learner website, quizzes, analytics and field deployment.',
  },
  strip: [
    { k: 'DEVELOPED BY',    v: 'Dr. Keyur Sorathia & team at the Embedded Interaction Lab, Department of Design, IIT Guwahati' },
    { k: 'CURRENT LEARNING',v: 'Grade 8–9 Science and Mathematics' },
    { k: 'LANGUAGES',       v: 'English · Hindi · Assamese' },
    { k: 'CORE OPERATION',  v: 'Offline-first' },
    { k: 'SOFTWARE ACCESS', v: 'No licence fee through June 2027' },
  ],
  what: {
    eyebrow: 'WHAT IT IS',
    title:   'A connected immersive learning ecosystem',
    desc:    'Gyan Dhara combines guided VR learning with continued practice, resources, AI support and progress visibility.',
    items: [
      { letter: 'VR', title: 'Learn in VR',        desc: 'Guided immersive modules for concepts that are difficult to understand through conventional explanation alone.', color: 'purple' },
      { letter: 'Q',  title: 'Practise and assess',desc: 'Unlimited conceptual, theoretical and applied quizzes connected to chapter learning.',                            color: 'royal' },
      { letter: 'R',  title: 'Continue learning',  desc: 'Chapter videos, visual images, visual PDFs and teacher-provided resources.',                                       color: 'cyan' },
      { letter: 'A',  title: 'Understand progress',desc: 'Completion, time spent and quiz performance for learners with individual accounts.',                                color: 'mint' },
    ],
  },
  experiences: {
    eyebrow: 'LEARNING EXPERIENCES',
    title:   'Curriculum and exploration',
    desc:    'The platform includes NCERT-aligned Science and Mathematics modules alongside optional journeys across space, history, art and nature.',
    items: [
      { title: 'Sound',            subject: 'Science' },
      { title: 'Combustion & Flame', subject: 'Science' },
      { title: 'Friction',         subject: 'Science' },
      { title: 'Perimeter',        subject: 'Mathematics' },
      { title: 'Area 2D',          subject: 'Mathematics' },
      { title: 'Volume',           subject: 'Mathematics' },
    ],
    beyondTitle: 'Explore beyond direct curriculum chapters',
    beyond:      'Taj Mahal · Qutub Minar · India Gate · Age of Dinosaurs · Living World Safari · Experience Space Station · 3D Painting',
    note:        'Exploratory experiences are clearly distinguished from direct NCERT-aligned learning modules.',
  },
  flow: {
    eyebrow: 'HOW THE ECOSYSTEM CONNECTS',
    title:   'One learning journey across VR and web',
    desc:    'A learner can experience the concept in VR, continue practising on the website and review progress through the appropriate dashboard.',
    steps: [
      { n: '1', title: 'Choose a concept',   desc: 'Curriculum module or exploratory journey.',      color: 'purple' },
      { n: '2', title: 'Learn in VR',        desc: 'One learner uses one headset at a time.',        color: 'royal' },
      { n: '3', title: 'Continue online',    desc: 'Videos, resources, quizzes and Gyan Mitra.',     color: 'cyan' },
      { n: '4', title: 'Review progress',    desc: 'Completion, time and performance summaries.',    color: 'mint' },
      { n: '5', title: 'Repeat and deepen',  desc: 'Revisit weaker areas or explore a new topic.',   color: 'purple' },
    ],
  },
  pathways: {
    eyebrow: 'CHOOSE YOUR PATHWAY LATER',
    title:   'The experience changes according to your role',
    desc:    'Explore freely now. When ready, choose the pathway that explains access, deployment or collaboration for your situation.',
    items: [
      { letter: 'S',  title: 'Student or Parent',        desc: 'Individual family learning and progress.',        color: 'royal',  to: '/student-parent' },
      { letter: 'SC', title: 'School or Institution',    desc: 'Classroom use, deployment and school operations.', color: 'purple', to: '/school' },
      { letter: 'CI', title: 'Coaching Institute',       desc: 'Scheduled learning and compact adoption.',        color: 'cyan',   to: '/coaching' },
      { letter: 'RU', title: 'Researcher or University', desc: 'Research activities, evidence and collaboration.', color: 'mint',   to: '/researcher' },
      { letter: 'P',  title: 'Government, CSR or Partner', desc: 'Programme scale, funding and implementation.',   color: 'purple', to: '/partners' },
    ],
  },
  access: {
    eyebrow: 'ACCESS AND COSTS',
    title:   'What is included and what is separate',
    desc:    'The complete Gyan Dhara software ecosystem is provided without a licence fee through June 2027. Hardware and deployment-related components are funded separately.',
    included: { title: 'Included software ecosystem', items: 'VR modules · learner website · quizzes · analytics · new releases · software updates' },
    funded:   { title: 'Separately funded',           items: 'Meta Quest 3S · straps · stools · configuration · courier · optional training · support · maintenance' },
  },
  cta: {
    title:   'Ready for information tailored to you?',
    desc:    'Choose a role to see the relevant access, deployment, evidence or partnership pathway.',
    primary: { label: 'Choose Your Role', to: '/' },
    secondary: { label: 'Request Access', to: '/explore/access-request' },
  },
};

// ===============================================================
// 133:2 VR EXPERIENCES (Learning Experiences)
// ===============================================================
export const EXPLORE_VR = {
  hero: {
    eyebrow: 'VR EXPERIENCES',
    title:   'Explore what students can learn and experience',
    desc:    'Browse the available curriculum modules and exploratory journeys without selecting a role. Access and deployment guidance can be chosen later.',
    ctaPrimary:   { label: 'Request Access',   to: '/explore/access-request' },
    ctaSecondary: { label: 'Choose Your Role', to: '/' },
    videoTag:  'IMMERSIVE PREVIEW',
    videoDesc: 'A short walkthrough of a VR module and its supporting web hub.',
  },
  curriculum: {
    eyebrow: 'CURRICULUM',
    title:   'Curriculum learning',
    desc:    'NCERT-aligned Grade 8 and 9 Science and Mathematics modules currently ready for use.',
    items: [
      { title: 'Sound',              subject: 'Science',     status: 'Available',    desc: 'Vibration, amplitude, pitch and sound in different media.' },
      { title: 'Combustion & Flame', subject: 'Science',     status: 'Available',    desc: 'Combustible materials and types of combustion.' },
      { title: 'Friction',           subject: 'Science',     status: 'Available',    desc: 'Friction, surfaces and real-world applications.' },
      { title: 'Perimeter',          subject: 'Mathematics', status: 'Available',    desc: 'Understand and calculate the perimeter of shapes.' },
      { title: 'Area 2D',            subject: 'Mathematics', status: 'Available',    desc: 'Calculate and compare areas of two-dimensional shapes.' },
      { title: 'Area 3D',            subject: 'Mathematics', status: 'Available',    desc: 'Build spatial understanding of three-dimensional area.' },
      { title: 'Volume',             subject: 'Mathematics', status: 'Available',    desc: 'Cube, cuboid and cylinder volume through interaction.' },
      { title: 'Zero Gravity',       subject: 'Science',     status: 'Available',    desc: 'Experience motion and orientation in microgravity.' },
      { title: "Gravity's Grip",     subject: 'Science',     status: 'Available',    desc: 'Understand gravitational pull through immersive interaction.' },
      { title: 'Work',               subject: 'Science',     status: 'Available',    desc: 'Explore the concept of work in physics.' },
      { title: 'Energy',             subject: 'Science',     status: 'Available',    desc: 'Kinetic, potential and energy transformations.' },
      { title: 'Falltopia',          subject: 'Science',     status: 'Available',    desc: 'Experience free-fall and gravitational motion.' },
      { title: 'AeroQuest',          subject: 'Science',     status: 'Available',    desc: 'Air, pressure and aerodynamics in motion.' },
      { title: 'Human Excretory System', subject: 'Science', status: 'Available',    desc: 'Anatomical journey through the excretory system.' },
    ],
  },
  exploration: {
    eyebrow: 'EXPLORATION AND GYAN VATIKA',
    title:   'Journeys beyond direct chapter alignment',
    desc:    'These experiences support curiosity and enrichment across space, history, art, science and nature.',
    items: [
      { title: 'Gyan Prangan',            subject: 'Gyan Vatika Preview' },
      { title: 'Taj Mahal',               subject: 'History & Heritage' },
      { title: 'Qutub Minar',             subject: 'History & Heritage' },
      { title: 'India Gate',              subject: 'History & Heritage' },
      { title: 'Age of Dinosaurs',        subject: 'Natural History' },
      { title: 'Lost World Expedition',   subject: 'Natural History' },
      { title: 'Living World Safari',     subject: 'Nature' },
      { title: 'Experience Space Station',subject: 'Space' },
      { title: '3D Painting',             subject: 'Art' },
      { title: 'Anne Frank Experience',   subject: 'Human Experience' },
      { title: 'Notes on Blindness',      subject: 'Human Experience' },
      { title: 'Pollinator Park',         subject: 'Nature' },
    ],
  },
  pipeline: {
    eyebrow: 'DEVELOPMENT PIPELINE',
    title:   'What is being prepared next',
    desc:    'Module availability and release timing will be updated as development and review are completed.',
    inDev: [
      { title: 'Work and Energy',              subject: 'Science' },
      { title: 'Contact and Non-contact Forces', subject: 'Science' },
      { title: 'Area of Cone',                 subject: 'Mathematics' },
      { title: 'Area of Sphere',               subject: 'Mathematics' },
      { title: 'Volume of Sphere and Cone',    subject: 'Mathematics' },
    ],
    upcoming: [
      { title: 'Structure of Atom', subject: 'Science' },
      { title: 'Light',             subject: 'Science' },
    ],
  },
  languages: {
    eyebrow: 'LANGUAGES AND FORMAT',
    title:   'Designed for accessible use',
    desc:    'The same learning modules are available in English, Hindi and Assamese, with core installed experiences designed for offline operation.',
    items: [
      { k: 'EN',  label: 'English',      desc: 'Functional across the ecosystem.',              color: 'purple' },
      { k: 'हि',  label: 'Hindi',        desc: 'Functional across modules and assessments.',    color: 'royal' },
      { k: 'অ',  label: 'Assamese',     desc: 'Functional across modules and assessments.',    color: 'cyan' },
      { k: 'OFF', label: 'Offline-first',desc: 'Core modules do not require internet.',          color: 'mint' },
    ],
  },
  cta: {
    title:   'Choose a role when you are ready to understand access or deployment.',
    desc:    'The learning content stays the same; the setup, support and collaboration pathway changes by role.',
    primary: { label: 'Request Access',   to: '/explore/access-request' },
    secondary: { label: 'Choose Your Role', to: '/' },
  },
};

// ===============================================================
// WEB LEARNING HUB (Explore version — mirrors Student pattern)
// ===============================================================
export const EXPLORE_HUB = {
  hero: {
    eyebrow: 'WEB LEARNING HUB',
    title:   'Continue every VR concept beyond the headset',
    desc:    'The Web Learning Hub extends every VR module with chapter videos, visual resources, unlimited quizzes and Gyan Mitra — all connected to one learner journey.',
    dashboard: { k: 'CURRENT CHAPTER', v: 'Friction', pk: 'Chapter progress', pv: '68%' },
  },
  connected: {
    eyebrow: 'CONNECTED LEARNING TOOLS',
    title:   'Everything that supports the VR session',
    desc:    'Six connected surfaces so learners can revisit, practise, ask and continue.',
    items: [
      { letter: 'V', title: 'Chapter videos',       desc: 'Short focused explainers that mirror each VR concept.',                       color: 'purple' },
      { letter: 'I', title: 'Visual images & PDFs', desc: 'Reference sheets, diagrams and printable summaries.',                          color: 'royal' },
      { letter: 'Q', title: 'Unlimited quizzes',    desc: 'Conceptual, theoretical and applied practice sets connected to each chapter.', color: 'cyan' },
      { letter: 'A', title: 'Analytics',            desc: 'Completion, time and performance by learning type.',                           color: 'mint' },
      { letter: 'G', title: 'Gyan Mitra',           desc: 'Verbal English, Hindi and Assamese Q&A whenever internet is available.',       color: 'purple' },
      { letter: 'R', title: 'Teacher resources',    desc: 'Institution-added references, worksheets and instructions.',                   color: 'royal' },
    ],
  },
  practice: {
    eyebrow: 'THREE QUIZ PATHWAYS',
    title:   'Practise what was learned in VR',
    desc:    'Every chapter carries three practice pathways so learners can strengthen conceptual clarity, apply what they learned and test recall.',
    types: [
      { letter: 'C', title: 'Conceptual', desc: 'Check core understanding of what was experienced in VR.',    color: 'purple' },
      { letter: 'A', title: 'Applied',    desc: 'Solve situations using the concept in familiar contexts.',    color: 'royal' },
      { letter: 'T', title: 'Theoretical',desc: 'Recall definitions, units and short explanations accurately.', color: 'cyan' },
    ],
    demo: {
      title:  'Friction · Applied practice',
      question: 'A wooden block slides down a smooth ramp with no measurable resistance. Which of the following best explains why?',
      options: [
        { k: 'A', v: 'Gravity is not acting on the block.',        correct: false },
        { k: 'B', v: 'The ramp surface is frictionless in this scenario.', correct: true },
        { k: 'C', v: 'The block has no mass.',                     correct: false },
        { k: 'D', v: 'The ramp is not inclined at all.',           correct: false },
      ],
      afterTitle: 'Why option B is correct',
      afterLines: [
        'A truly smooth surface implies no friction between the surfaces in contact.',
        'Gravity still acts on the block; that is why it accelerates down the ramp.',
        'Frictional force resists relative motion; when it is absent, the block slides freely.',
      ],
    },
  },
  progress: {
    eyebrow: 'PROGRESS AT A GLANCE',
    title:   'Every session updates the same learner story',
    desc:    'When a learner uses an individual account, VR activity, quiz attempts and chapter time are grouped into one clear progress view.',
    overviewTitle: 'Session overview',
    metrics: [
      { label: 'MODULES COMPLETED',  k: '5' },
      { label: 'TIME SPENT',         k: '3h 42m' },
      { label: 'QUIZ ATTEMPTS',      k: '18' },
    ],
    performance: [
      { label: 'Conceptual', v: 78 },
      { label: 'Applied',    v: 66 },
      { label: 'Theoretical',v: 74 },
    ],
    recent: [
      { title: 'Friction · VR module',       status: 'Completed' },
      { title: 'Friction · Applied quiz',    status: 'In progress' },
      { title: 'Combustion & Flame · Chapter video', status: 'Recommended' },
    ],
  },
  cta: {
    title:   'The hub complements every VR concept.',
    desc:    'Request access to explore the full learning ecosystem — VR + web.',
    primary: { label: 'Request Access',   to: '/explore/access-request' },
    secondary: { label: 'Choose Your Role', to: '/' },
  },
};

// ===============================================================
// EXPLORE ACCESS REQUEST
// ===============================================================
export const EXPLORE_ACCESS = {
  hero: {
    eyebrow: 'REQUEST ACCESS',
    title:   'Get access to Gyan Dhara — without picking a role yet',
    desc:    'Share your context and the team will guide you through the pathway that matches your situation — school, family, coaching, research or programme partnership.',
    softwareCard: {
      title: 'No licence fee through June 2027',
      lines: [
        'The complete software ecosystem — VR modules, learner website, quizzes, analytics, updates — is provided without a licence fee.',
        'Hardware, accessories, configuration, courier and optional training are separately funded.',
      ],
    },
  },
  form: {
    title:    'Access request',
    hint:     'All fields marked * are required.',
    roles:    ['Student', 'Parent or guardian', 'Educator', 'Institution representative', 'Partner or funder', 'Researcher', 'Other'],
    grades:   ['Grade 8', 'Grade 9', 'Other'],
    interests:['Science', 'Mathematics', 'Exploration'],
    quest:    ['Yes', 'No', 'Planning to purchase'],
    langs:    ['English', 'Hindi', 'Assamese'],
    submit:   'Submit Access Request',
  },
  next: [
    { n: 1, title: 'Team review',       desc: 'Context, geography and intent.' },
    { n: 2, title: 'Guided discussion', desc: 'Match to the right pathway.' },
    { n: 3, title: 'Setup planning',    desc: 'Hardware, accounts and orientation.' },
    { n: 4, title: 'Launch',            desc: 'First VR session and hub walkthrough.' },
    { n: 5, title: 'Follow-up',         desc: 'Progress check, feedback and next steps.' },
  ],
  consider: [
    { n: 1, title: 'Who will use it',      desc: 'A learner, family, class or group.' },
    { n: 2, title: 'Where',                desc: 'Home, classroom, coaching centre or programme.' },
    { n: 3, title: 'Hardware readiness',   desc: 'Existing headset, purchase plan or programme-funded.' },
    { n: 4, title: 'Time available',       desc: 'Preferred session length and frequency.' },
  ],
};
